import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

/**
 * Tabs component — accessible tablist + tabpanel (Sprint AQ、docs/16 §10-2 P3 H10 UI6)。
 *
 * WAI-ARIA Authoring Practices の Tabs pattern を踏襲:
 *   - role="tablist" / role="tab" / role="tabpanel"
 *   - aria-selected / aria-controls / aria-labelledby
 *   - tab に tabIndex (active=0 / others=-1) で roving tabindex
 *   - keyboard nav: ArrowLeft / ArrowRight で前後移動 (wrap)、Home / End で先頭 / 末尾
 *
 * controlled / uncontrolled 両対応:
 *   - `activeId` 指定で controlled (parent が制御)
 *   - 未指定 + `defaultActive` で uncontrolled (内部 state)
 *   - `defaultActive` も無ければ最初の tab を active
 *
 * styling は shared-ui 既存 token (primary / slate) に合わせた最低限。
 * 旧 v1 に Tabs component の前例は無いため (PostDetailModal は単一 column 表示) 新規追加。
 */

export interface TabItem {
  /** tab を識別する ID (URL fragment や test selector にも使える). */
  id: string;
  /** tab button に表示するラベル. */
  label: ReactNode;
  /** tab panel の内容. */
  content: ReactNode;
  /** 無効化フラグ (disabled tab は keyboard nav でも skip). */
  disabled?: boolean;
}

export interface TabsProps {
  tabs: TabItem[];
  /** controlled active tab id. 指定時は uncontrolled state を無視. */
  activeId?: string;
  /** uncontrolled の初期 active tab id. 未指定なら最初の tab. */
  defaultActive?: string;
  /** active tab 変更時のコールバック. */
  onChange?: (id: string) => void;
  /** tablist の aria-label (or aria-labelledby を別途付ける場合は省略可). */
  'aria-label'?: string;
  /** tablist の外側に付与する className. */
  className?: string;
  /**
   * 見た目。'underline' (既定) = 下線のタブ、'folder' = 内容を囲む枠とつながったフォルダ型のタブ
   * (何が切り替わるかを見せたい所、例: 投稿詳細の AI 解析の観点、docs/29)。
   */
  variant?: 'underline' | 'folder';
}

const TAB_BASE =
  'px-3 py-1.5 -mb-px text-sm font-medium border-b-2 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-1 transition-colors';
const TAB_ACTIVE = 'border-primary-600 text-primary-700';
const TAB_INACTIVE =
  'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300';
const TAB_DISABLED = 'border-transparent text-slate-300 cursor-not-allowed';

// folder: 選んでいるタブの下の線を内容の枠の背景色で消し、タブと内容を一続きに見せる。
const FOLDER_LIST = 'relative z-10 flex flex-wrap items-end gap-1 pl-2';
const FOLDER_BASE =
  '-mb-px min-w-0 rounded-t-lg border px-3 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary-400';
const FOLDER_ACTIVE = 'border-primary-400 border-b-primary-50 bg-primary-50 text-primary-800';
const FOLDER_INACTIVE =
  'border-slate-200 bg-slate-100 text-slate-500 hover:bg-white hover:text-primary-700';
const FOLDER_DISABLED = 'border-slate-200 bg-slate-50 text-slate-300 cursor-not-allowed';
const FOLDER_PANEL = 'rounded-lg border border-primary-400 bg-primary-50 p-3';

export function Tabs({
  tabs,
  activeId,
  defaultActive,
  onChange,
  'aria-label': ariaLabel,
  className,
  variant = 'underline',
}: TabsProps) {
  const fallbackId = tabs.find((t) => !t.disabled)?.id ?? tabs[0]?.id ?? '';
  const [internalActive, setInternalActive] = useState<string>(defaultActive ?? fallbackId);
  const current = activeId ?? internalActive;
  const baseId = useId();
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // controlled → uncontrolled fallback の整合: tabs から消えた active を防御。
  useEffect(() => {
    if (activeId !== undefined) return;
    if (!tabs.some((t) => t.id === internalActive && !t.disabled)) {
      setInternalActive(fallbackId);
    }
  }, [tabs, activeId, internalActive, fallbackId]);

  function setActive(id: string) {
    if (activeId === undefined) setInternalActive(id);
    onChange?.(id);
  }

  function focusTab(id: string) {
    tabRefs.current[id]?.focus();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const enabled = tabs.filter((t) => !t.disabled);
    if (enabled.length === 0) return;
    const currentIdx = enabled.findIndex((t) => t.id === current);
    const idx = currentIdx >= 0 ? currentIdx : 0;
    let next: number | null = null;
    switch (e.key) {
      case 'ArrowRight':
        next = (idx + 1) % enabled.length;
        break;
      case 'ArrowLeft':
        next = (idx - 1 + enabled.length) % enabled.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = enabled.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    const target = enabled[next];
    if (!target) return;
    setActive(target.id);
    focusTab(target.id);
  }

  if (tabs.length === 0) return null;

  return (
    <div className={className}>
      <div
        role="tablist"
        aria-label={ariaLabel}
        className={variant === 'folder' ? FOLDER_LIST : 'flex border-b border-slate-200 gap-1'}
        onKeyDown={handleKeyDown}
      >
        {tabs.map((t) => {
          const selected = t.id === current;
          const cls =
            variant === 'folder'
              ? `${FOLDER_BASE} ${t.disabled ? FOLDER_DISABLED : selected ? FOLDER_ACTIVE : FOLDER_INACTIVE}`
              : t.disabled
                ? `${TAB_BASE} ${TAB_DISABLED}`
                : `${TAB_BASE} ${selected ? TAB_ACTIVE : TAB_INACTIVE}`;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`${baseId}-tab-${t.id}`}
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${t.id}`}
              tabIndex={selected ? 0 : -1}
              disabled={t.disabled}
              ref={(el) => {
                tabRefs.current[t.id] = el;
              }}
              onClick={() => {
                if (t.disabled) return;
                setActive(t.id);
              }}
              className={cls}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      {tabs.map((t) => {
        const selected = t.id === current;
        return (
          <div
            key={t.id}
            role="tabpanel"
            id={`${baseId}-panel-${t.id}`}
            aria-labelledby={`${baseId}-tab-${t.id}`}
            hidden={!selected}
            className={variant === 'folder' ? FOLDER_PANEL : 'pt-3'}
          >
            {selected ? t.content : null}
          </div>
        );
      })}
    </div>
  );
}
